import React from 'react';
import PropTypes from 'prop-types';
import cloudIcon from '../../icons/cloud.svg';
import successIcon from '../../icons/success.svg';
import failedIcon from '../../icons/failed.svg';
import copyIcon from '../../icons/copy.svg';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import './DNSTable.css';

const WIREFRAME_ROWS = 2;

function DNSTable({
    dnsRecords,
    showStatus = false,
    showCloudflare = false,
    boxClass = 'domain-form',
    nameLabel = 'Name',
    preview = false,
    loading = false,
    children,
}) {
    const [copiedValue, copy] = useCopyToClipboard();
    const success = <img src={successIcon} alt="Success" />;
    const failed = <img src={failedIcon} alt="Failed" />;
    const showWireframe = preview || loading;

    return (
        <section id="step-2" className={boxClass}>
            <div className="dns-table">
                <div className="dns-table-content">
                    <p className="dns-table-key">
                        <strong>Type</strong>
                    </p>
                    <p className="dns-table-name">
                        <strong>{nameLabel}</strong>
                    </p>
                    <p className="dns-table-value">
                        <strong>Value</strong>
                    </p>
                    {/* The extra columns are only known once the records are
                        determined, so they're hidden from the wireframe */}
                    {showCloudflare && !showWireframe && (
                        <p className="dns-table-cfproxy">
                            <strong>Proxy Status</strong>
                        </p>
                    )}
                    {showStatus && !showWireframe && (
                        <p className="dns-table-status">
                            <strong>Status</strong>
                        </p>
                    )}

                    {showWireframe &&
                        Array.from({ length: WIREFRAME_ROWS }, (_, row) => (
                            <React.Fragment key={row}>
                                <p className="dns-table-key" data-testid="dns-wireframe-row">
                                    <span className="dns-wireframe-bar short" />
                                </p>
                                <p className="dns-table-name">
                                    <span className="dns-wireframe-bar medium" />
                                </p>
                                <p className="dns-table-value">
                                    <span className="dns-wireframe-bar long" />
                                </p>
                            </React.Fragment>
                        ))}

                    {!showWireframe &&
                        dnsRecords?.map(({ record_type: record, name, value, valid, error }) => {
                            const recordKey = record + '-' + value;

                            return (
                                <React.Fragment key={recordKey}>
                                    <p className="dns-table-key">{record}</p>
                                    {name ? (
                                        <p className="dns-table-name" onClick={() => copy(name)}>
                                            {name}
                                            {copiedValue && copiedValue === name ? (
                                                <button className="copy-btn green">
                                                    <img src={successIcon} alt="Copied" />
                                                </button>
                                            ) : (
                                                <button className="copy-btn">
                                                    <img src={copyIcon} alt="Copy" />
                                                </button>
                                            )}
                                        </p>
                                    ) : (
                                        <p>&nbsp;</p>
                                    )}
                                    <p className="dns-table-value" onClick={() => copy(value)}>
                                        {value}
                                        {copiedValue && copiedValue === value ? (
                                            <button className="copy-btn green">
                                                <img src={successIcon} alt="Copied" />
                                            </button>
                                        ) : (
                                            <button className="copy-btn">
                                                <img src={copyIcon} alt="Copy" />
                                            </button>
                                        )}
                                    </p>
                                    {showCloudflare && (
                                        <p className="dns-table-cfproxy">
                                            <img
                                                className="max-h-16"
                                                src={cloudIcon}
                                                alt="DNS Only"
                                            />
                                            DNS Only
                                        </p>
                                    )}
                                    {showStatus && (
                                        <p className="dns-table-status">
                                            {valid === 'valid' ? success : failed}
                                        </p>
                                    )}

                                    {valid === 'invalid' && error && (
                                        <div className="dns-table-error">
                                            <p>{error}</p>
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                </div>
                {loading && (
                    <div className="dns-table-loading" data-testid="dns-table-spinner">
                        <div className="gh-spinner"></div>
                    </div>
                )}
            </div>
            {children}
        </section>
    );
}

DNSTable.propTypes = {
    dnsRecords: PropTypes.arrayOf(
        PropTypes.shape({
            record_type: PropTypes.string,
            name: PropTypes.string,
            value: PropTypes.string,
            valid: PropTypes.oneOf(['valid', 'unknown', 'invalid']),
            error: PropTypes.string,
        }),
    ).isRequired,
    showStatus: PropTypes.bool,
    showCloudflare: PropTypes.bool,
    preview: PropTypes.bool,
    loading: PropTypes.bool,
    boxClass: PropTypes.string,
    nameLabel: PropTypes.string,
    children: PropTypes.node,
};

export default DNSTable;
